import { Box, Grid, Flex, Text, Link, VStack, HStack, Button, Image, useColorModeValue, useBreakpointValue } from '@chakra-ui/react';
import React from 'react';
import { useAccountEffect } from 'wagmi';

import config from 'configs/app';
import { copy } from 'lib/html-entities';
import { responsive } from 'lib/responsive';
import { useExplorerConfig } from 'lib/contexts/ExplorerConfigContext';
import useToast from 'lib/hooks/useToast';
import useAccount from 'lib/web3/useAccount';
import IconSvg from 'ui/shared/IconSvg';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import { openAppKitModal, isAppKitReady } from 'ui/shared/Web3ModalProvider';

// Social platform prefixes (same as admin panel)
const SOCIAL_PREFIXES: Record<string, { prefix: string; icon: string }> = {
  twitter: { prefix: 'https://x.com/', icon: 'social/twitter_filled' },
  telegram: { prefix: 'https://t.me/', icon: 'social/telegram_filled' },
  discord: { prefix: 'https://discord.gg/', icon: 'social/discord_filled' },
  github: { prefix: 'https://github.com/', icon: 'social/github_filled' },
  medium: { prefix: 'https://medium.com/@', icon: 'social/medium_filled' },
  reddit: { prefix: 'https://reddit.com/r/', icon: 'social/reddit_filled' },
  facebook: { prefix: 'https://facebook.com/', icon: 'social/facebook_filled' },
  instagram: { prefix: 'https://instagram.com/', icon: 'social/instagram_filled' },
  youtube: { prefix: 'https://youtube.com/@', icon: 'social/youtube_filled' },
};

const SOCIAL_LABELS: Record<string, string> = {
  twitter: 'X (Twitter)', telegram: 'Telegram', discord: 'Discord', github: 'GitHub',
  medium: 'Medium', reddit: 'Reddit', facebook: 'Facebook', instagram: 'Instagram', youtube: 'YouTube',
};

const Footer = () => {
  const { config: explorerConfig } = useExplorerConfig();
  const toast = useToast();
  const account = useAccount();
  const [ footerMounted, setFooterMounted ] = React.useState(false);

  React.useEffect(() => {
    setFooterMounted(true);
  }, []);

  const pendingAddChain = React.useRef(false);
  const [ isAdding, setIsAdding ] = React.useState(false);

  const envLogoUrl = config.UI.navigation.logo.default || config.UI.navigation.logo.dark || '';
  const footerLogoUrl = explorerConfig?.branding?.footer_logo_url || envLogoUrl;
  const poweredByText = explorerConfig?.general?.powered_by_text || 'Powered by DataCore Smart Chain';
  const poweredByLink = explorerConfig?.general?.powered_by_link;
  const footerDescription = explorerConfig?.general?.footer_description ||
    `${ config.chain.name } Smart Chain Explorer is a Block Explorer and Analytics Platform for DataCore Smart Chain, a decentralized smart contracts Platform.`;
  const addNetworkText = explorerConfig?.general?.add_network_button_text || `Add ${ config.chain.name } Network`;
  const addNetworkLogoUrl = explorerConfig?.general?.add_network_logo_url;
  const showAddNetworkButton = explorerConfig?.general?.show_add_network_button !== false;
  const donationAddress = explorerConfig?.general?.donation_address;
  const showDonation = explorerConfig?.general?.show_donation !== false;

  // Build social links from explorerConfig (usernames → full URLs)
  const socialLinksArray = React.useMemo(() => {
    const links = explorerConfig?.social_links;
    if (!links) return [];
    return Object.entries(links)
      .filter(([ , username ]) => username && String(username).trim().length > 0)
      .map(([ platform, username ]) => {
        const info = SOCIAL_PREFIXES[platform];
        if (!info) return null;
        const usernameStr = String(username).trim();
        const fullUrl = usernameStr.startsWith('http') ? usernameStr : `${ info.prefix }${ usernameStr }`;
        return { platform, url: fullUrl, icon: info.icon, label: SOCIAL_LABELS[platform] || platform };
      })
      .filter(Boolean) as Array<{ platform: string; url: string; icon: string; label: string }>;
  }, [ explorerConfig?.social_links ]);

  const defaultSocialLinks = [
    { platform: 'twitter', icon: 'social/twitter_filled', url: 'https://x.com/dscchain', label: 'X (Twitter)' },
    { platform: 'medium', icon: 'social/medium_filled', url: 'https://medium.com/@dscchain', label: 'Medium' },
    { platform: 'facebook', icon: 'social/facebook_filled', url: 'https://facebook.com/dscchain', label: 'Facebook' },
    { platform: 'reddit', icon: 'social/reddit_filled', url: 'https://reddit.com/r/dscchain', label: 'Reddit' },
  ];

  const socialLinks = (footerMounted && socialLinksArray.length > 0) ? socialLinksArray : defaultSocialLinks;
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [ hasMounted, setHasMounted ] = React.useState(false);
  React.useEffect(() => setHasMounted(true), []);
  const socialLinksToShow = (hasMounted && isMobile) ? socialLinks.slice(0, 2) : socialLinks;

  // Get chain config from admin settings or env
  const getChainParams = React.useCallback(() => {
    const networkConfig = explorerConfig?.network;
    const chainId = networkConfig?.chain_id || config.chain.id;
    const networkName = networkConfig?.network_name || config.chain.name || 'Unknown Chain';
    const rpcUrl = networkConfig?.rpc_url || (config.chain.rpcUrls?.[0]) || '';
    const nativeSymbol = networkConfig?.native_token_symbol || config.chain.currency?.symbol || 'ETH';
    const nativeName = networkConfig?.native_token_symbol || config.chain.currency?.name || 'ETH';
    const nativeDecimals = Number(networkConfig?.native_token_decimals) || config.chain.currency?.decimals || 18;
    return { chainId, networkName, rpcUrl, nativeSymbol, nativeName, nativeDecimals };
  }, [ explorerConfig?.network ]);

  // Add chain via any ethereum provider
  const addChainViaProvider = React.useCallback(async(ethereumProvider?: any) => {
    const prov = ethereumProvider || (window as any).ethereum;
    if (!prov) {
      // eslint-disable-next-line no-console
      console.log('[AddNetwork] No ethereum provider available');
      return false;
    }

    const { chainId, networkName, rpcUrl, nativeSymbol, nativeName, nativeDecimals } = getChainParams();
    if (!chainId || !rpcUrl) {
      toast({
        position: 'top-right',
        title: 'Configuration Error',
        description: 'Chain ID and RPC URL are required in admin Network settings.',
        status: 'error',
        variant: 'subtle',
        isClosable: true,
      });
      return false;
    }

    const hexChainId = '0x' + Number(chainId).toString(16);

    try {
      await prov.request({
        method: 'wallet_switchEthereumChain',
        params: [ { chainId: hexChainId } ],
      });
      toast({ position: 'top-right', title: 'Network Switched!', description: `Switched to ${ networkName }`, status: 'success', variant: 'subtle', isClosable: true });
      return true;
    } catch (switchError: any) {
      if (switchError?.code === 4902 || switchError?.message?.includes('Unrecognized')) {
        try {
          await prov.request({
            method: 'wallet_addEthereumChain',
            params: [ {
              chainId: hexChainId,
              chainName: networkName,
              nativeCurrency: { name: nativeName, symbol: nativeSymbol, decimals: nativeDecimals },
              rpcUrls: [ rpcUrl ],
              blockExplorerUrls: [ window.location.origin ],
            } ],
          });
          toast({ position: 'top-right', title: 'Network Added!', description: `${ networkName } added to your wallet`, status: 'success', variant: 'subtle', isClosable: true });
          return true;
        } catch (addErr: any) {
          toast({ position: 'top-right', title: 'Failed', description: addErr?.message || 'User rejected', status: 'error', variant: 'subtle', isClosable: true });
          return false;
        }
      }
      toast({ position: 'top-right', title: 'Error', description: switchError?.message || 'Failed', status: 'error', variant: 'subtle', isClosable: true });
      return false;
    }
  }, [ getChainParams, toast ]);

  // After wallet connects via AppKit QR, automatically add chain
  useAccountEffect({
    onConnect: React.useCallback(({ isReconnected }: { isReconnected: boolean }) => {
      if (!isReconnected && pendingAddChain.current) {
        pendingAddChain.current = false;
        setTimeout(() => {
          addChainViaProvider();
        }, 1500);
      }
    }, [ addChainViaProvider ]),
  });

  // ===== MAIN CLICK HANDLER =====
  const handleAddNetwork = React.useCallback(async() => {
    setIsAdding(true);

    const hasEthereum = !!(window as any).ethereum;
    const appKitReady = isAppKitReady();

    // eslint-disable-next-line no-console
    console.log('[AddNetwork] Click!', {
      accountConnected: account.isConnected,
      hasEthereum,
      appKitReady,
    });

    try {
      // PATH A: If window.ethereum is available (MetaMask/Brave/etc extension installed)
      // → add chain directly via the extension — this shows the MetaMask popup
      if (hasEthereum) {
        // eslint-disable-next-line no-console
        console.log('[AddNetwork] Using window.ethereum provider directly');
        await addChainViaProvider();
        setIsAdding(false);
        return;
      }

      // PATH B: No extension — use AppKit modal (WalletConnect QR code)
      if (appKitReady) {
        // eslint-disable-next-line no-console
        console.log('[AddNetwork] Opening AppKit wallet connect modal...');
        pendingAddChain.current = true;
        toast({
          position: 'top-right',
          title: 'Connect Wallet',
          description: 'Scan the QR code with your mobile wallet to connect & add the network',
          status: 'info',
          variant: 'subtle',
          isClosable: true,
          duration: 4000,
        });
        await openAppKitModal();
        // After connection, useAccountEffect will automatically add the chain
        setIsAdding(false);
        return;
      }

      // PATH C: Nothing available
      // eslint-disable-next-line no-console
      console.log('[AddNetwork] No wallet or AppKit available');
      toast({
        position: 'top-right',
        title: 'No wallet detected',
        description: 'Install MetaMask extension or use a Web3 browser to add the network.',
        status: 'warning',
        variant: 'subtle',
        isClosable: true,
        duration: 5000,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AddNetwork] Error:', err);
      toast({
        position: 'top-right',
        title: 'Error',
        description: (err as Error)?.message || 'Something went wrong',
        status: 'error',
        variant: 'subtle',
        isClosable: true,
      });
    } finally {
      setIsAdding(false);
    }
  }, [ account.isConnected, addChainViaProvider, toast ]);

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const shortenAddress = (addr: string) => {
    if (addr.length <= 14) return addr;
    return addr.slice(0, 8) + '...' + addr.slice(-6);
  };

  const COMPANY_LINKS = [
    { text: 'About Us', url: '/about' },
    { text: 'Brand Assets', url: '/brand-assets' },
    { text: 'Contact Us', url: '/contact' },
    { text: 'Careers', url: '/careers', badge: 'We\'re Hiring!' },
    { text: 'Terms & Privacy', url: '/terms' },
    { text: 'Bug Bounty', url: '/bug-bounty' },
  ];

  const COMMUNITY_LINKS = [
    { text: 'API Documentation', url: '/api-docs' },
    { text: 'Knowledge Base', url: '/knowledge-base' },
    { text: 'Network Status', url: '/status' },
    { text: 'Newsletters', url: '/newsletters' },
  ];

  const PRODUCTS_LINKS = [
    { text: 'Advertise', url: '/advertise' },
    { text: 'Explorer as a Service (EaaS)', url: 'https://www.nadcab.com', external: true },
    { text: 'API Plans', url: '/api-plans' },
    { text: 'Priority Support', url: '/priority-support' },
    { text: 'Explorer Home', url: '/' },
  ];

  const backToTopRightIcon = <IconSvg name="arrows/up-head" boxSize={ 3 }/>;
  const footerBg = useColorModeValue('#F8F9FA', 'gray.900');
  const topBarBorder = useColorModeValue('#E5E7EB', 'gray.600');
  const footerText = useColorModeValue('#343A40', 'white');
  const footerTextMuted = useColorModeValue('#6C757D', 'gray.300');
  const footerLink = useColorModeValue('#0D6EFD', 'blue.400');
  const addNetworkBg = useColorModeValue('#ffffff', 'gray.700');
  const stakingBadgeBg = useColorModeValue('#2E84C2', 'blue.500');
  const socialCircleBg = useColorModeValue('#E9ECEF', 'gray.600');
  const socialCircleHoverBg = useColorModeValue('#DEE2E6', 'gray.500');
  const socialIconColor = useColorModeValue('#343A40', 'gray.200');

  return (
    <Box as="footer" bg={ footerBg } w="100%" suppressHydrationWarning>
      { /* Top bar: full-width border, content in max-width area */ }
      <Box w="100%" borderBottomWidth="1px" borderBottomColor={ topBarBorder }>
        <Flex
          maxW={ `${ CONTENT_MAX_WIDTH }px` }
          mx="auto"
          px={{ base: 4, lg: 6 }}
          py={{ base: 4, lg: 5 }}
          justifyContent="space-between"
          alignItems="center"
          flexWrap="nowrap"
          gap={ 3 }
          suppressHydrationWarning
        >
          <HStack spacing={ 3 } flexShrink={ 0 } suppressHydrationWarning>
            { socialLinksToShow.map((social) => (
              <Link
                key={ social.platform }
                href={ social.url }
                isExternal
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="34px"
                h="34px"
                borderRadius="full"
                bg={ socialCircleBg }
                color={ socialIconColor }
                _hover={{ opacity: 0.85, bg: socialCircleHoverBg }}
                transition="all 0.2s"
                aria-label={ social.label }
              >
                <IconSvg name={ social.icon as any } boxSize={ 5 } color={ socialIconColor }/>
              </Link>
            )) }
          </HStack>

          <Button
            variant="ghost"
            size="sm"
            onClick={ scrollToTop } // eslint-disable-line react/jsx-no-bind
            fontSize={{ base: '13px', md: '14px' }}
            fontWeight="400"
            color={ footerText }
            _hover={{ color: footerLink }}
            leftIcon={ backToTopRightIcon }
            flexShrink={ 0 }
          >
            Back to Top
          </Button>
        </Flex>
      </Box>

      { /* Main Footer Content: mobile = row1 full width (Powered By), row2 two cols (Company | Community), row3 one col (Products) */ }
      <Box py={{ base: 6, lg: 8 }} px={{ base: 4, lg: 6 }} w="100%" maxW={{ base: '100%', lg: '1400px' }} mx="auto">
        <Grid
          w="100%"
          templateColumns={{ base: '1fr 1fr', md: 'repeat(2, 1fr)', lg: '2fr 1fr 1fr 1.5fr' }}
          gap={{ base: 6, md: 8, lg: 12 }}
        >
          <Box gridColumn={{ base: '1 / -1', md: 'auto' }} suppressHydrationWarning>
            <Flex alignItems="center" mb={{ base: 2, md: 3 }} suppressHydrationWarning>
              { footerLogoUrl ? (
                <Image src={ footerLogoUrl } alt="Footer logo" h={{ base: '28px', sm: '32px', md: '36px' }} w="auto" maxW="200px" objectFit="contain" mr={{ base: 2, md: 3 }} />
              ) : (
                <Box w={ 9 } h={ 9 } borderRadius="md" bgGradient="linear(to-br, yellow.400, orange.300)" display="flex" alignItems="center" justifyContent="center" mr={{ base: 2, md: 3 }} flexShrink={ 0 }>
                  <Text color="white" fontWeight="bold" fontSize={ responsive.body }>◆</Text>
                </Box>
              ) }
              { poweredByLink ? (
                <Link href={ poweredByLink } isExternal className="footer-brand-title" fontWeight="700" fontSize={ responsive.sectionTitle } color={ footerText } _hover={{ color: footerLink, textDecoration: 'underline' }}>
                  { poweredByText }
                </Link>
              ) : (
                <Text className="footer-brand-title" fontWeight="700" fontSize={ responsive.sectionTitle } color={ footerText } suppressHydrationWarning>{ poweredByText }</Text>
              ) }
            </Flex>
            <Text fontSize={ responsive.body } color={ footerTextMuted } lineHeight="1.6" mb={{ base: 4, md: 5 }} suppressHydrationWarning>{ footerDescription }</Text>
            { showAddNetworkButton && (
              <Button
                size="sm"
                variant="outline"
                bg={ addNetworkBg }
                borderColor={ topBarBorder }
                color={ footerText }
                fontWeight="500"
                fontSize={ responsive.body }
                borderRadius="8px"
                px={ 4 }
                py={ 2 }
                h="auto"
                _hover={{ borderColor: footerLink, opacity: 0.9 }}
                onClick={ handleAddNetwork }
                isLoading={ isAdding }
                loadingText="Connecting..."
                leftIcon={
                  addNetworkLogoUrl ? (
                    <Image src={ addNetworkLogoUrl } boxSize={ 5 } borderRadius="2px" />
                  ) : (
                    <IconSvg name="wallets/metamask" boxSize={ 5 } />
                  )
                }
              >
                { addNetworkText }
              </Button>
            ) }
          </Box>

          <Box pt={{ base: 6, md: 0 }} borderTopWidth={{ base: '1px', md: 0 }} borderTopColor={ topBarBorder }>
            <Text fontWeight="600" color={ footerText } fontSize={ responsive.sectionTitle } mb={{ base: 3, md: 4 }}>Company</Text>
            <VStack align="start" spacing={{ base: 2, md: 3 }}>
              { COMPANY_LINKS.map((link) => (
                <Flex key={ link.text } alignItems="center" gap={ 2 }>
                  <Link href={ link.url } fontSize={ responsive.bodySmall } fontWeight="400" color={ footerTextMuted } _hover={{ color: footerLink, textDecoration: 'underline' }}>{ link.text }</Link>
                  { link.badge && (
                    <Box px={ 2 } py={ 0.5 } bg={ stakingBadgeBg } color="white" fontSize={ responsive.caption } borderRadius="12px" fontWeight="500">{ link.badge }</Box>
                  ) }
                </Flex>
              )) }
            </VStack>
          </Box>

          <Box pt={{ base: 6, md: 0 }} borderTopWidth={{ base: '1px', md: 0 }} borderTopColor={ topBarBorder }>
            <Text fontWeight="600" color={ footerText } fontSize={ responsive.sectionTitle } mb={{ base: 3, md: 4 }}>Community</Text>
            <VStack align="start" spacing={{ base: 2, md: 3 }}>
              { COMMUNITY_LINKS.map((link) => (
                <Link key={ link.text } href={ link.url } fontSize={ responsive.bodySmall } color={ footerTextMuted } _hover={{ color: footerLink, textDecoration: 'underline' }}>{ link.text }</Link>
              )) }
            </VStack>
          </Box>

          <Box gridColumn={{ base: '1 / -1', md: 'auto' }} pt={{ base: 6, md: 0 }} borderTopWidth={{ base: '1px', md: 0 }} borderTopColor={ topBarBorder }>
            <Text fontWeight="600" color={ footerText } fontSize={ responsive.sectionTitle } mb={{ base: 3, md: 4 }}>Products & Services</Text>
            <VStack align="start" spacing={{ base: 2, md: 3 }}>
              { PRODUCTS_LINKS.map((link) => (
                <Flex key={ link.text } alignItems="center" gap={ 1.5 }>
                  <Link href={ link.url } fontSize={ responsive.body } color={ footerTextMuted } _hover={{ color: footerLink, textDecoration: 'underline' }} isExternal={ link.external }>{ link.text }</Link>
                  { link.external && <IconSvg name="link_external" boxSize={ 3 } color={ footerTextMuted }/> }
                </Flex>
              )) }
            </VStack>
          </Box>
        </Grid>
      </Box>

      { /* Bottom bar: mobile = stacked or one line with pipe separator (BscScan-style) */ }
      <Box w="100%" borderTopWidth="1px" borderTopColor={ topBarBorder }>
        <Flex
          maxW={ `${ CONTENT_MAX_WIDTH }px` }
          mx="auto"
          px={{ base: 4, lg: 6 }}
          py={ 4 }
          flexDirection={{ base: 'column', sm: 'row' }}
          alignItems={{ base: 'flex-start', sm: 'center' }}
          flexWrap="wrap"
          gap={{ base: 3, sm: 2 }}
          suppressHydrationWarning
        >
          <Text fontSize={ responsive.caption } color="text_primary" suppressHydrationWarning>
            { explorerConfig?.general?.explorer_name || `${config.chain.name || 'Matato'} Explorer` } { copy } { (new Date()).getFullYear() } (B1)
          </Text>
          { showDonation && donationAddress && (
            <>
              <Text as="span" fontSize={ responsive.caption } color={ footerTextMuted } display={{ base: 'none', sm: 'inline' }} suppressHydrationWarning>|</Text>
              <Flex alignItems="center" gap={ 2 } fontSize={ responsive.caption } color={ footerTextMuted } flexWrap="wrap" suppressHydrationWarning>
                <Text as="span">Donations:</Text>
                <Link href={ `/address/${ donationAddress }` } color={ footerLink } _hover={{ textDecoration: 'underline' }}>
                  { shortenAddress(donationAddress) }
                </Link>
                <Text as="span" color="red.500" fontSize={ responsive.body } lineHeight="1" flexShrink={ 0 } display="inline-block" aria-label="Heart">
                  {'\u2764\uFE0F'}
                </Text>
              </Flex>
            </>
          ) }
        </Flex>
      </Box>
    </Box>
  );
};

export default React.memo(Footer);
